import React, { useState } from 'react';

import { Grid, Typography, Card, CardContent, Button, Box, Link } from '@mui/material';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import ReplayIcon from '@mui/icons-material/Replay';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { RateAnalysisCard } from '../../../HOC/Cards';
import { UserListingInfoBar } from 'src/App/HOC';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { Compsets, Listings } from 'src/interfaces/idashboard';
import {
  AVAILABLE_NIGHT_COLOR,
  BOOKED_NIGHT_COLOR,
  COLOR_ACCENT_2,
  COLOR_GREEN,
  COMPETITOR_RATES_COLORS,
  COLOR_PRIMARY_DARK,
  exchangeRates,
} from 'src/const';
import { CompsetDashboardPropTypes } from 'src/interfaces/iproptypes';
import {
  formatCompetitorRatesForRecharts,
  getCompMetaData,
  getCompetitorRates,
  getCompsDataRows,
  getLastNDates,
  getProcessedDataForDynamicPricing,
  getnextNDates,
  splitStringToList,
} from 'src/utils/functions';
import {
  COLOR_BACKGROUND_2,
  COLOR_ERROR_1,
  COLOR_ERROR_1_HOVER,
  COLOR_GREY_200,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_1_HOVER,
  COLOR_TEXT_2,
} from 'src/colors';
import { callApi } from 'src/core/callApi';
import { type } from 'os';

const d3 = require('d3');

const CompsetDashboard = ({
  listingPricingRecommendation,
  manageListingModalContentRef,
  noOfFutureDays,
}: CompsetDashboardPropTypes) => {
  const medianKeys = ['Median Available Rate', 'Median Booked Rate'];
  const medianColors = {
    'Median Available Rate': AVAILABLE_NIGHT_COLOR,
    'Median Booked Rate': BOOKED_NIGHT_COLOR,
  };
  const unprocessed_saved_comps = listingPricingRecommendation.saved_comps;
  const saved_comps = splitStringToList(unprocessed_saved_comps);
  const dateList: string[] = listingPricingRecommendation.comp_dict.date_list;
  const compRatesObj = listingPricingRecommendation.comp_dict.rate_and_avail_changes;
  const compMetadata: Listings = getCompMetaData(
    listingPricingRecommendation.comp_listings_metadata
  );
  const compsets: Compsets = getCompetitorRates(compRatesObj, dateList, compMetadata);

  const DataRows = getCompsDataRows(compsets, listingPricingRecommendation);
  const top10CompsAsPerOccupancyRate = DataRows.slice(0, 10).map(row => row.id);
  const defaultSelectedComps = saved_comps.length > 0 ? saved_comps : top10CompsAsPerOccupancyRate;
  const listingDetails = listingPricingRecommendation.listing_details;
  const realTimeUpdateStatus = getRealTimeUpdateStatus(listingPricingRecommendation.comp_dict);
  const [selectedComps, setSelectedComps] = useState<string[]>(defaultSelectedComps);
  const [selectedCompsFromDataGrid, setSelectedCompsFromDataGrid] = useState<string[]>(
    defaultSelectedComps
  );
  const [compsUpdatedDialogBoxOpen, setCompsUpdatedDialogBoxOpen] = React.useState(false);
  const [compsUndoDialogBoxOpen, setCompsUndoDialogBoxOpen] = React.useState(false);
  const colors = {
    25: '#E74645',
    50: '#DBA507',
    75: '#FF4D94',
    90: '#0D6986',
  };

  let listingComparisionTableData = getProcessedDataForDynamicPricing(
    [listingPricingRecommendation],
    noOfFutureDays,
    'revPARDiff',
    selectedComps
  );

  // console.log("listingComparisionTableData",listingComparisionTableData)

  const dataColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Listing Id',
      width: 180,
      editable: false,
      sortable: false,
    },
    {
      field: 'link',
      headerName: 'Link',
      width: 110,
      editable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Link href={'https://www.airbnb.co.in/rooms/' + params.id} target="_blank">
            {params.row.link}
          </Link>
        );
      },
    },
    {
      field: 'distance',
      headerName: 'Distance (mi)',
      type: 'number',
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      type: 'number',
      sortable: false,
      editable: false,
      width: 150,
    },
    {
      field: 'starRating',
      headerName: 'Star Rating',
      type: 'number',
      sortable: true,
      editable: false,
      width: 150,
    },
    {
      field: 'adr',
      headerName: 'ADR in $ (Next 30 days)',
      type: 'number',
      sortable: true,
      editable: false,
      width: 200,
    },
    {
      field: 'occupancyRate',
      headerName: 'Occupancy Rate (Next 30 days)',
      type: 'number',
      sortable: true,
      editable: false,
      width: 250,
    },
  ];

  const {
    competitormedianRatesRecharts,
    marketLevelmedianOccupancyRate,
    marketLevelmedianBookedRate,
  } = formatCompetitorRatesForRecharts(
    compsets,
    dateList,
    selectedComps,
    listingPricingRecommendation.listing_details.listingID
  );

  const handleCheckBoxClickInCompsDataGrid = newSelection => {
    setSelectedCompsFromDataGrid(newSelection);
  };

  const handleCompsUpdatedDialogBoxClose = () => {
    setCompsUpdatedDialogBoxOpen(false);
  };

  const handleCompsUndoDialogBoxClose = () => {
    setCompsUndoDialogBoxOpen(false);
  };

  const handleCompsUpdatedDialogBoxOpen = () => {
    setCompsUpdatedDialogBoxOpen(true);
  };

  const handleCompsUndoDialogBoxOpen = () => {
    setCompsUndoDialogBoxOpen(true);
  };

  const getRechartsMetaData = competitormedianRatesRecharts => {
    let rechartMetaData = {};
    let keys = Object.keys(competitormedianRatesRecharts[0]);
    keys.forEach(key => {
      if (key != 'date' && key != 'median_booked_rate' && key != 'user_listing_rate') {
        let tempObj = {
          name: key + 'th percentile',
          color: colors[key],
        };
        rechartMetaData[key] = tempObj;
      }
    });
    return rechartMetaData;
  };

  const getPastAndFutureRates = (competitormedianRatesRecharts, listingPricingRecommendation) => {
    let pastNdates = getLastNDates(noOfFutureDays);
    let futureNdates = getnextNDates(noOfFutureDays);
    // console.log("futureNdates",futureNdates) 
    let historical_data = listingPricingRecommendation['historical_data'];
    let format_historical_data: any[] = [];
    let pastAndFutureRates: any[] = [];
    for (const date in historical_data) {
      if (pastNdates.includes(date)) {
        const dailyRatesOfCompset = historical_data[date];
        let rates: any[] = [];
        let booked_rates: any[] = [];
        let userListingRate;
        for (const id in dailyRatesOfCompset) {
          if (id !== 'userListingId' && selectedComps.includes(id)) {
            let price = dailyRatesOfCompset[id].split('-')[0];
            let status = dailyRatesOfCompset[id].split('-')[1];
            rates.push(price);
            if (status == 'booked') {
              booked_rates.push(price);
            }
          } else if (id === 'userListingId') {
            let price = dailyRatesOfCompset[id].split('-')[0];
            userListingRate = price;
          }
        }
        let dailyRatePercentileHistoricalData = {
          90: Math.round(d3.quantile(rates, 0.9)),
          75: Math.round(d3.quantile(rates, 0.75)),
          50: Math.round(d3.quantile(rates, 0.5)),
          25: Math.round(d3.quantile(rates, 0.25)),
          median_booked_rate: Math.round(d3.quantile(booked_rates, 0.5)) || 0,
          user_listing_rate: userListingRate ? userListingRate : undefined,
          date: date,
        };
        format_historical_data.push(dailyRatePercentileHistoricalData);
      }
    }
    pastAndFutureRates = [...format_historical_data];
    const currentDate = new Date();
    for (let key in competitormedianRatesRecharts) {
      let value = competitormedianRatesRecharts[key].date;
      let date = new Date(value);
      if (futureNdates.includes(date)) {
        pastAndFutureRates.push(competitormedianRatesRecharts[key]);
      }
    }
    return pastAndFutureRates;
  };
  const pastAndFutureRates = getPastAndFutureRates(
    competitormedianRatesRecharts,
    listingPricingRecommendation
  );

  let rechart_metaData = getRechartsMetaData(competitormedianRatesRecharts);

  const handleSaveCompList = () => {
    if (selectedCompsFromDataGrid.length == 0) {
      setSelectedComps(top10CompsAsPerOccupancyRate);
      setSelectedCompsFromDataGrid(top10CompsAsPerOccupancyRate);
      saveCompsToDataBase(top10CompsAsPerOccupancyRate);
    } else {
      setSelectedComps(selectedCompsFromDataGrid);
      saveCompsToDataBase(selectedCompsFromDataGrid);
    }
    handleCompsUpdatedDialogBoxClose();
    manageListingModalContentRef.current.scrollTo({
      top: 150,
      behavior: 'smooth',
    });
  };

  const saveCompsToDataBase = compsToBeSavedInDB => {
    const compsetCRUDUrl =
      'https://jjosc8ay07.execute-api.us-east-2.amazonaws.com/default/compsetCRUD';
    const config = {
      queryParams: {
        listingID: listingPricingRecommendation.listing_details.listingID,
        operation: 'UPDATE_SAVED_COMPS',
        selectedComps: `${compsToBeSavedInDB}`,
      },
    };
    callApi(compsetCRUDUrl, config, false);
  };

  const handleUndoButton = () => {
    setSelectedComps(top10CompsAsPerOccupancyRate);
    setSelectedCompsFromDataGrid(top10CompsAsPerOccupancyRate);
    saveCompsToDataBase(top10CompsAsPerOccupancyRate);
    handleCompsUndoDialogBoxClose();
    manageListingModalContentRef.current.scrollTo({
      top: 100,
      behavior: 'smooth',
    });
  };

  const CompareCompetitorPricesTooltipBody = ({
    payload,
    date,
    formatDate,
    showMedian,
    selectedFilters,
  }: {
    payload: any;
    date: Date;
    formatDate: (dateString: Date, showDay?: boolean) => string;
    showMedian: boolean;
    selectedFilters: string[];
  }) => {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)' }}>
        <CardContent
          sx={{
            background: 'white',
            width: '270px',
            height: `${payload.length * 6}vh`,
            overflowY: 'hidden',
            overflowX: 'hidden',
          }}
        >
          <Typography
            variant="h6"
            sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_PRIMARY_DARK }}
          >
            {formatDate(date, true)}
          </Typography>
          {payload &&
            payload.map(currentPayload => {
              if (selectedFilters.includes(currentPayload['dataKey'])) {
                return (
                  <Typography
                    variant="body1"
                    sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_TEXT_2 }}
                  >
                    <>
                      {'Daily Rate(' + currentPayload['name'] + 'th percentile): '}
                      <span style={{ color: COLOR_PRIMARY_DARK }}>
                        {exchangeRates['USD'].symbol}
                        {currentPayload['payload'][currentPayload['name']]}
                      </span>{' '}
                    </>
                  </Typography>
                );
              } else if (currentPayload['dataKey'] == 'median_booked_rate') {
                return (
                  <Typography
                    variant="body1"
                    sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_TEXT_2 }}
                  >
                    <>
                      {'Median Booked Rate: '}
                      <span style={{ color: COLOR_PRIMARY_DARK }}>
                        {exchangeRates['USD'].symbol}
                        {currentPayload['payload'][currentPayload['name']]}
                      </span>{' '}
                    </>
                  </Typography>
                );
              } else if (currentPayload['dataKey'] == 'user_listing_rate') {
                return (
                  <Typography
                    variant="body1"
                    sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_TEXT_2 }}
                  >
                    <>
                      {'Your Booking Rate: '}
                      <span style={{ color: COLOR_PRIMARY_DARK }}>
                        {currentPayload['payload'][currentPayload['name']] != 'N/A' &&
                          exchangeRates['USD'].symbol}
                        {currentPayload['payload'][currentPayload['name']]}
                      </span>{' '}
                    </>
                  </Typography>
                );
              } else {
                return <></>;
              }
            })}
          {/* {!showMedian &&
            selectedFilters
              .sort((a, b) => compsets[b].rates[date].rate - compsets[a].rates[date].rate)
              .map((item, index) => {
                const status = compsets[item].rates[date].status;
                const isBooked = status === 'booked' || status === 'recentlyBooked';
                return (
                  <Grid
                    item
                    container
                    direction="row"
                    alignItems="center"
                    sx={{
                      background: isBooked
                        ? `repeating-linear-gradient(
                        -45deg,
                        ${compsets[item].color}60,
                        ${compsets[item].color}20 3px,
                        ${compsets[item].color}20 3px,
                        ${compsets[item].color}20 5px
                      )`
                        : `${compsets[item].color}20`,
                      userMixBlendMode: 'multiply',
                      height: 35,
                      borderRadius: 10,
                      border: `2px solid ${compsets[item].color}`,
                      marginBottom: 2,
                      '&:last-child': {
                        marginBottom: 0,
                      },
                      padding: '0 20px',
                      overflow: 'visible',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      position: 'relative',
                      top: 0,
                      left: 0,
                    }}
                  >
                    {isBooked && (
                      <Typography
                        sx={{
                          borderRadius: 5,
                          background: compsets[item].color,
                          position: 'absolute',
                          left: '5px',
                          top: '-12px',
                          color: 'white',
                          padding: '0 10px',
                          fontWeight: 'bold',
                          fontSize: '10px',
                        }}
                      >
                        Booked
                      </Typography>
                    )}

                    <Typography
                      key={index}
                      sx={{
                        color: 'black',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'center',
                      }}
                      variant="body2"
                    >
                      <span style={{ fontWeight: 'bold' }}>${compsets[item].rates[date].rate}</span>{' '}
                      <span style={{ color: DARK_GREY, margin: '0 10px' }}>|</span>{' '}
                      {compsets[item].name}
                    </Typography>
                  </Grid>
                );
              })}

          {showMedian &&
            medianKeys.map((item, index) => (
              <Grid
                item
                container
                direction="row"
                alignItems="center"
                sx={{
                  backgroundColor: `${medianColors[item]}20`,
                  userMixBlendMode: 'multiply',
                  height: 35,
                  borderRadius: 10,
                  border: `2px solid ${medianColors[item]}`,
                  marginBottom: 1,
                  '&:last-child': {
                    marginBottom: 0,
                  },
                  padding: '0 20px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography
                  key={index}
                  sx={{
                    color: 'black',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'center',
                  }}
                  variant="body2"
                >
                  <span style={{ fontWeight: 'bold' }}>${payload[0].payload[item]}</span>{' '}
                  <span style={{ color: DARK_GREY, margin: '0 10px' }}>|</span> {item}
                </Typography>
              </Grid>
            ))} */}
        </CardContent>
      </Card>
    );
  };

  const DialogBoxForCompsDataGrid = ({ open, closeFunction, agreeFunction, content }) => {
    return (
      <Dialog
        open={open}
        onClose={closeFunction}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Typography variant="body1" sx={{ fontWeight: 400, color: 'black' }}>
              {content + ' '}
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'black' }}>
                Competitor Price Trends
              </Typography>
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              padding: '0px 16px',
              borderRadius: '25px',
              backgroundColor: COLOR_ERROR_1,
              color: COLOR_BACKGROUND_2,
              marginTop: '5px',
              ':hover': {
                backgroundColor: COLOR_ERROR_1_HOVER,
              },
            }}
            onClick={closeFunction}
          >
            Disagree
          </Button>
          <Button
            sx={{
              padding: '0px 16px',
              borderRadius: '25px',
              backgroundColor: COLOR_SUCCESS_1,
              color: COLOR_BACKGROUND_2,
              marginTop: '5px',
              ':hover': {
                backgroundColor: COLOR_SUCCESS_1_HOVER,
              },
            }}
            onClick={agreeFunction}
            autoFocus
          >
            Agree
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <Grid container direction="column" spacing={2} sx={{ minHeight: '1000px', padding: '10px' }}>
        <Grid
          item
          xs={12}
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid
            item
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            xs={8}
          >
            <UserListingInfoBar listing={listingDetails} isUserListing={true} />
          </Grid>

          <Grid container item xs={3} direction="column" alignItems="flex-end">
            <Typography sx={{ color: COLOR_GREEN }}>
              Updated {realTimeUpdateStatus.lastUpdateHourDiff} hours{' '}
              {realTimeUpdateStatus.lastUpdateMinuteDiff} minutes ago
            </Typography>
            <Typography sx={{ color: COLOR_ACCENT_2 }}>
              Next update in {realTimeUpdateStatus.nextUpdateHourDiff} hours{' '}
              {realTimeUpdateStatus.nextUpdateMinuteDiff} minutes
            </Typography>
          </Grid>
        </Grid>

        <Grid container item xs={12}>
          <Box
            sx={{
              backgroundColor: COLOR_GREY_200,
              height: '100%',
              width: '100%',
              borderRadius: '10px',
            }}
          >
            <Typography variant="h5" sx={{ padding: '10px 0px 0px 20px' }}>
              Next {noOfFutureDays} Days
            </Typography>
            <Grid
              container
              sx={{
                margin: '10px 0px 0px 105px',
                padding: '0px 25px',
              }}
              gap={5}
            >
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  Comparison
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  Occupancy
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  Booked Rates
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  RevPAR
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  Availability
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={{
                margin: '10px 0px 0px 105px',
                padding: '0px 25px',
              }}
              gap={5}
            >
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  Listing Average
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: '500',
                    color: `${
                      Number(listingComparisionTableData[0].listingOccupancy) >
                      Number(listingComparisionTableData[0].compsMedianOccupancy)
                        ? 'green'
                        : 'red'
                    }`,
                  }}
                >
                  {listingComparisionTableData[0].listingOccupancy}%
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: '500',
                    color: `${
                      Number(listingComparisionTableData[0].listingBookingRate) >
                      Number(listingComparisionTableData[0].compsMedianBookingRate)
                        ? 'green'
                        : 'red'
                    }`,
                  }}
                >
                  ${listingComparisionTableData[0].listingBookingRate}
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: '500',
                    color: `${
                      Number(listingComparisionTableData[0].listingRevPAR) >
                      Number(listingComparisionTableData[0].compsMedianRevPAR)
                        ? 'green'
                        : 'red'
                    }`,
                  }}
                >
                  ${listingComparisionTableData[0].listingRevPAR}
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: '500',
                    color: `${
                      Number(listingComparisionTableData[0].listingAvailiableCountPercentage) >
                      Number(listingComparisionTableData[0].compsAvailiableCountPercentage)
                        ? 'green'
                        : 'red'
                    }`,
                  }}
                >
                  {listingComparisionTableData[0].listingAvailiableCountPercentage}%
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={{
                margin: '10px 0px 10px 105px',
                padding: '0px 25px',
              }}
              gap={5}
            >
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  Comps Average
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  {listingComparisionTableData[0].compsMedianOccupancy}%
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  ${listingComparisionTableData[0].compsMedianBookingRate}
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  ${listingComparisionTableData[0].compsMedianRevPAR}
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="h6" sx={{ fontWeight: '500' }}>
                  {listingComparisionTableData[0].compsAvailiableCountPercentage}%
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <RateAnalysisCard
            title="Competitor Price Trends"
            titleMedianSwitch="Median Booked Price"
            data={pastAndFutureRates}
            monthlyData={pastAndFutureRates}
            metaData={rechart_metaData}
            currency="USD"
            dateList={dateList}
            getMedianData={() => ({
              medianData: [],
              medianDataRange: { minData: 0, maxData: 100 },
            })}
            medianKeys={medianKeys}
            medianColors={medianColors}
            TooltipBody={CompareCompetitorPricesTooltipBody}
          />
        </Grid>

        <Grid item container xs={12} direction="column" spacing={2}>
          {/* heading & toolbar */}
          <Grid item container direction="row" alignItems="center" justifyContent="space-between">
            <Grid
              item
              xs={6}
              container
              spacing={1}
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="h6">Compsets Used for Pricing</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item container xl={12} lg={12} md={12} xs={12}>
            <Box sx={{ height: 550, width: '100%', position: 'relative' }}>
              <DataGrid
                rows={DataRows}
                columns={dataColumns}
                checkboxSelection={true}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
                hideFooter={false}
                disableColumnMenu={true}
                pagination
                pageSize={25}
                selectionModel={selectedCompsFromDataGrid}
                onSelectionModelChange={handleCheckBoxClickInCompsDataGrid}
                sx={{
                  background: '#fff',
                  borderRadius: '20px',
                  padding: '10px 20px',
                  '& .MuiDataGrid-iconSeparator': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: COLOR_PRIMARY_DARK,
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-cellContent': {
                    fontWeight: 500,
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(93, 82, 107)', //rgba(167, 113, 255)
                    cursor: 'pointer',
                  },
                  '& .MuiTablePagination-root': {
                    overflow: 'hidden',
                  },
                  '& .MuiToolbar-root ': {
                    padding: '0px !important',
                  },
                }}
              />
            </Box>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={handleCompsUndoDialogBoxOpen}
                sx={{
                  padding: '0px 16px',
                  borderRadius: '25px',
                  backgroundColor: COLOR_ERROR_1,
                  color: COLOR_BACKGROUND_2,
                  marginTop: '5px',
                  ':hover': {
                    backgroundColor: COLOR_ERROR_1_HOVER,
                  },
                }}
              >
                <ReplayIcon />
                <Typography sx={{ marginLeft: '5px' }}>Undo Changes</Typography>
              </Button>
              <Button
                onClick={handleCompsUpdatedDialogBoxOpen}
                sx={{
                  padding: '5px 16px',
                  borderRadius: '25px',
                  backgroundColor: COLOR_SUCCESS_1,
                  color: COLOR_BACKGROUND_2,
                  marginTop: '5px',
                  ':hover': {
                    backgroundColor: COLOR_SUCCESS_1_HOVER,
                  },
                }}
              >
                <SaveAsIcon />
                <Typography sx={{ marginLeft: '5px' }}>Save Comp List</Typography>
              </Button>
            </Box>
          </Grid>
        </Grid>
        <DialogBoxForCompsDataGrid
          open={compsUpdatedDialogBoxOpen}
          closeFunction={handleCompsUpdatedDialogBoxClose}
          agreeFunction={handleSaveCompList}
          content="Your Comps have been updated. Visit the graph to observe"
        />
        <DialogBoxForCompsDataGrid
          open={compsUndoDialogBoxOpen}
          closeFunction={handleCompsUndoDialogBoxClose}
          agreeFunction={handleUndoButton}
          content="Default Comps will be selected. Visit the graph to observe"
        />
      </Grid>
    </>
  );
};

const getRealTimeUpdateStatus = compRatesObj => {
  const currentSnapshot = compRatesObj.current_snapshot;
  const nextUpdate = compRatesObj.next_update;

  const date = new Date();
  const currentSnapshotObj = currentSnapshot.split('-');
  const nextUpdateObj = nextUpdate.split('-');
  const weeklyLastUpdateDate = new Date(
    parseInt(currentSnapshotObj[0]),
    parseInt(currentSnapshotObj[1]) - 1,
    parseInt(currentSnapshotObj[2])
  ).setUTCHours(15);
  const weeklyNextUpdateDate = new Date(
    parseInt(nextUpdateObj[0]),
    parseInt(nextUpdateObj[1]) - 1,
    parseInt(nextUpdateObj[2])
  ).setUTCHours(15);

  const weeklyLastUpdateTimeDiff = Math.round(
    Math.abs(weeklyLastUpdateDate - date.getTime()) / 60000
  );
  const weeklyNextUpdateTimeDiff = Math.round(
    Math.abs(date.getTime() - weeklyNextUpdateDate) / 60000
  );
  const lastUpdateHourDiff = Math.round(weeklyLastUpdateTimeDiff / 60);
  const lastUpdateMinuteDiff = weeklyLastUpdateTimeDiff % 60;
  const nextUpdateHourDiff = Math.round(weeklyNextUpdateTimeDiff / 60);
  const nextUpdateMinuteDiff = weeklyNextUpdateTimeDiff % 60;

  return { lastUpdateHourDiff, lastUpdateMinuteDiff, nextUpdateHourDiff, nextUpdateMinuteDiff };
};

export default CompsetDashboard;
